import React, { FC, useEffect } from 'react';

import { AssistantChatMessage, AssistantNavigationTarget } from '../../../data-access';
import { AssistantChatCtaButtonsList } from '../AssistantChatCtaButtonsList';
import { NAVIGATION_TARGET_ACTIONS } from './navigationTargets';

// An auto-navigate only makes sense for a message that just arrived. Older
// messages (history reload, list remount) must never re-trigger a screen open.
const AUTO_NAVIGATE_MAX_AGE_MS = 15_000;
const autoNavigatedMessageIds = new Set<string>();

interface AssistantChatNavigationButtonProps {
  item: AssistantChatMessage;
  onNavigate: (target: AssistantNavigationTarget) => void;
}

/** v4-only: a `navigate` event rendered as a tappable "open this screen" button. */
const AssistantChatNavigationButton: FC<AssistantChatNavigationButtonProps> = ({
  item,
  onNavigate,
}) => {
  const navigation = item.navigation;
  const supported = !!navigation && !!NAVIGATION_TARGET_ACTIONS[navigation.target];

  useEffect(() => {
    if (!navigation?.auto || !supported || autoNavigatedMessageIds.has(item.messageId)) {
      return;
    }
    autoNavigatedMessageIds.add(item.messageId);
    const ageMs = Date.now() - new Date(item.sentAt).getTime();
    if (ageMs <= AUTO_NAVIGATE_MAX_AGE_MS) {
      onNavigate(navigation.target);
    }
  }, [item.messageId, item.sentAt, navigation, onNavigate, supported]);

  if (!navigation || !supported) {
    return null;
  }

  return (
    <AssistantChatCtaButtonsList
      buttons={[{ id: navigation.target, text: navigation.label }]}
      onButtonPress={() => onNavigate(navigation.target)}
    />
  );
};

export default AssistantChatNavigationButton;
